// Market Segmentation Canvas — shared types + Zod schemas
//
// Single source of truth for field shapes, grounding metadata, and AI IO contracts.
// API routes validate inputs with these schemas; the service layer parses AI outputs with them.

import { z } from 'zod'

// ============================================================================
// PROVENANCE (source tracking per field)
// ============================================================================

export const FIELD_SOURCES = ['ai', 'manual', 'edited'] as const
export type FieldSource = (typeof FIELD_SOURCES)[number]

export const fieldSourceSchema = z.enum(FIELD_SOURCES)

// Sizing fields hold a numeric value (nullable) + provenance.
export const sizingFieldSchema = z.object({
  value: z.number().nullable(),
  source: fieldSourceSchema,
  aiValue: z.number().nullable(),
  rationale: z.string().optional(),
  modelUsed: z.string().optional(),
  generatedAt: z.string().datetime().optional(),
  editedAt: z.string().datetime().optional(),
})
export type SizingField = z.infer<typeof sizingFieldSchema>

// Score fields additionally carry rationale + grounded entity URIs (KG citations).
export const groundedEntitySchema = z.object({
  uri: z.string(),
  type: z.string(),
  label: z.string(),
})
export type GroundedEntity = z.infer<typeof groundedEntitySchema>

export const scoreFieldSchema = z.object({
  value: z.number().min(0).max(10).nullable(),
  source: fieldSourceSchema,
  aiValue: z.number().min(0).max(10).nullable(),
  rationale: z.string().optional(),
  modelUsed: z.string().optional(),
  groundedEntities: z.array(groundedEntitySchema).default([]),
  generatedAt: z.string().datetime().optional(),
  editedAt: z.string().datetime().optional(),
})
export type ScoreField = z.infer<typeof scoreFieldSchema>

// ============================================================================
// SIZING — 5 fields per spec: TAM / SAM (top-down) / SAM (bottom-up) / SOM / CAGR
// ============================================================================

export const SIZING_FIELD_KEYS = [
  'tam',
  'samTopDown',
  'samBottomUp',
  'som',
  'cagr',
] as const
export type SizingFieldKey = (typeof SIZING_FIELD_KEYS)[number]

export const segmentSizingSchema = z.object({
  tam: sizingFieldSchema,
  samTopDown: sizingFieldSchema,
  samBottomUp: sizingFieldSchema,
  som: sizingFieldSchema,
  cagr: sizingFieldSchema,
})
export type SegmentSizing = z.infer<typeof segmentSizingSchema>

export const EMPTY_SIZING_FIELD: SizingField = {
  value: null,
  source: 'manual',
  aiValue: null,
}
export const EMPTY_SEGMENT_SIZING: SegmentSizing = {
  tam: EMPTY_SIZING_FIELD,
  samTopDown: EMPTY_SIZING_FIELD,
  samBottomUp: EMPTY_SIZING_FIELD,
  som: EMPTY_SIZING_FIELD,
  cagr: EMPTY_SIZING_FIELD,
}

// ============================================================================
// SCORING — 6 criteria per spec: Fit / Size / Growth / Serve / Compete / Profit
// ============================================================================

export const SCORING_FIELD_KEYS = [
  'strategicFit',
  'marketSize',
  'marketGrowth',
  'easyToServe',
  'easyToCompete',
  'profitPotential',
] as const
export type ScoringFieldKey = (typeof SCORING_FIELD_KEYS)[number]

export const segmentScoringSchema = z.object({
  strategicFit: scoreFieldSchema,
  marketSize: scoreFieldSchema,
  marketGrowth: scoreFieldSchema,
  easyToServe: scoreFieldSchema,
  easyToCompete: scoreFieldSchema,
  profitPotential: scoreFieldSchema,
})
export type SegmentScoring = z.infer<typeof segmentScoringSchema>

export const EMPTY_SCORE_FIELD: ScoreField = {
  value: null,
  source: 'manual',
  aiValue: null,
  groundedEntities: [],
}
export const EMPTY_SEGMENT_SCORING: SegmentScoring = {
  strategicFit: EMPTY_SCORE_FIELD,
  marketSize: EMPTY_SCORE_FIELD,
  marketGrowth: EMPTY_SCORE_FIELD,
  easyToServe: EMPTY_SCORE_FIELD,
  easyToCompete: EMPTY_SCORE_FIELD,
  profitPotential: EMPTY_SCORE_FIELD,
}

export const CRITERIA_LABELS: Record<
  ScoringFieldKey,
  { label: string; description: string; groundingHint: string }
> = {
  strategicFit: {
    label: 'Strategic fit',
    description: 'How well the segment aligns with our VPC jobs/pains/gains and BMC.',
    groundingHint: 'Cite VPC or BMC entities.',
  },
  marketSize: {
    label: 'Market size',
    description: 'Absolute market opportunity in this segment.',
    groundingHint: 'Cite Source/Document or industry report.',
  },
  marketGrowth: {
    label: 'Market growth',
    description: 'Expected CAGR and directional trend.',
    groundingHint: 'Cite Source/Document.',
  },
  easyToServe: {
    label: 'Easy to serve',
    description: 'Fit with our capabilities, channels, delivery model.',
    groundingHint: 'Cite Capability or BMC channel.',
  },
  easyToCompete: {
    label: 'Easy to compete',
    description: 'Competitive intensity and whitespace available.',
    groundingHint: 'Cite Competitor entities (or explicit "none tracked").',
  },
  profitPotential: {
    label: 'Profit potential',
    description: 'Deal economics, willingness to pay, unit economics.',
    groundingHint: 'Cite BMC revenue stream or comparable deal.',
  },
}

// ============================================================================
// SECTOR / STATUS / ROLE / COVERAGE ENUMS
// ============================================================================

export const SECTORS = ['private', 'public', 'cross'] as const
export type Sector = (typeof SECTORS)[number]

export const SEGMENT_STATUSES = ['draft', 'active', 'archived'] as const
export type SegmentStatus = (typeof SEGMENT_STATUSES)[number]

export const PERSONA_ROLES = [
  'economic_buyer',
  'technical_buyer',
  'user',
  'influencer',
] as const
export type PersonaRole = (typeof PERSONA_ROLES)[number]

export const COVERAGE_LEVELS = ['primary', 'secondary', 'tertiary'] as const
export type CoverageLevel = (typeof COVERAGE_LEVELS)[number]

// ============================================================================
// WEIGHTS
// ============================================================================

export const weightsSchema = z.object({
  strategicFit: z.number().min(0).max(1),
  marketSize: z.number().min(0).max(1),
  marketGrowth: z.number().min(0).max(1),
  easyToServe: z.number().min(0).max(1),
  easyToCompete: z.number().min(0).max(1),
  profitPotential: z.number().min(0).max(1),
})
export type Weights = z.infer<typeof weightsSchema>

export const DEFAULT_WEIGHTS: Weights = {
  strategicFit: 0.2,
  marketSize: 0.2,
  marketGrowth: 0.15,
  easyToServe: 0.15,
  easyToCompete: 0.15,
  profitPotential: 0.15,
}

// ============================================================================
// COLOR RAMP (for heatmap cells — UI references these, server doesn't care)
// ============================================================================

export const COLOR_RAMP_THRESHOLDS = [
  { min: 7, max: 10, token: '--color-scale-green' },
  { min: 4, max: 6, token: '--color-scale-amber' },
  { min: 0, max: 3, token: '--color-scale-red' },
] as const

// ============================================================================
// BRIEF CONTENT (9 sections)
// ============================================================================

export const BRIEF_SECTIONS = [
  'summary',
  'definition',
  'drivers',
  'growth',
  'competitive',
  'buyers',
  'regulatory',
  'strategicFit',
  'risks',
] as const
export type BriefSection = (typeof BRIEF_SECTIONS)[number]

export const briefContentSchema = z.object({
  summary: z.string(),
  definition: z.string(),
  drivers: z.string(),
  growth: z.string(),
  competitive: z.string(),
  buyers: z.string(),
  regulatory: z.string(),
  strategicFit: z.string(),
  risks: z.string(),
})
export type MarketBriefContent = z.infer<typeof briefContentSchema>

// ============================================================================
// DOMAIN TYPES
// ============================================================================

export interface MarketSegmentationCanvas {
  id: string
  projectId: string
  accountId: string
  weights: Weights
  beachheadSegmentId: string | null
  settings: Record<string, unknown>
  createdAt: string
  updatedAt: string
}

export interface MarketSegment {
  id: string
  projectId: string
  accountId: string
  canvasId: string
  name: string
  sector: Sector
  vertical: string | null
  description: string | null
  status: SegmentStatus
  sortOrder: number
  sizing: SegmentSizing
  scoring: SegmentScoring
  notes: string | null
  /** URI of the backing CustomerSegment entity in the knowledge graph, if sourced from intelligence. */
  graphEntityUri?: string | null
  createdAt: string
  updatedAt: string
}

/** Derived, computed server-side on read — not persisted. */
export interface MarketSegmentWithScore extends MarketSegment {
  weightedScore: number
}

export interface MarketPersona {
  id: string
  projectId: string
  accountId: string
  canvasId: string
  name: string
  role: PersonaRole
  description: string | null
  linkedWorkshopPersonaId: string | null
  /** URI of the backing Persona entity in the knowledge graph, if sourced from intelligence. */
  graphEntityUri?: string | null
  sortOrder: number
  createdAt: string
  updatedAt: string
}

export interface SegmentPersonaLink {
  segmentId: string
  personaId: string
  coverage: CoverageLevel
  notes: string | null
  createdAt: string
}

export interface MarketBrief {
  id: string
  projectId: string
  accountId: string
  segmentId: string
  version: number
  content: MarketBriefContent
  supersededById: string | null
  isCurrent: boolean
  generatedAt: string
  modelUsed: string | null
  promptVersion: string
  createdBy: string | null
  createdAt: string
}

/** Lightweight metadata for version lists. */
export interface MarketBriefSummary {
  id: string
  segmentId: string
  version: number
  isCurrent: boolean
  generatedAt: string
  modelUsed: string | null
  supersededById: string | null
}

export interface MarketEntityLink {
  id: string
  projectId: string
  targetType: 'brief' | 'score_field'
  targetId: string
  sectionOrField: string | null
  entityUri: string
  entityType: string
  entityLabel: string
  linkType: 'reference' | 'derived' | 'supports'
  contextNote: string | null
  sortOrder: number
  createdById: string | null
  createdAt: string
}

// ============================================================================
// AI OUTPUT SCHEMAS (Zod) — service layer parses model output with these
// ============================================================================

/** Sparse fill: AI may emit numeric value or null for any sizing field. */
export const sizingAIOutputSchema = z.object({
  tam: z.number().nullable(),
  samTopDown: z.number().nullable(),
  samBottomUp: z.number().nullable(),
  som: z.number().nullable(),
  cagr: z.number().nullable(),
  _notes: z.string().optional(),
})
export type SizingAIOutput = z.infer<typeof sizingAIOutputSchema>

/**
 * A single scored criterion from AI. Numeric values must carry rationale + ≥1
 * grounded entity; null values may have empty groundedEntities.
 * Service-layer validator enforces domain-specific grounding requirements.
 */
const scoringCriterionOutputSchema = z.object({
  value: z.number().min(0).max(10).nullable(),
  rationale: z.string().optional(),
  groundedEntities: z.array(groundedEntitySchema).default([]),
})

export const scoringAIOutputSchema = z.object({
  strategicFit: scoringCriterionOutputSchema,
  marketSize: scoringCriterionOutputSchema,
  marketGrowth: scoringCriterionOutputSchema,
  easyToServe: scoringCriterionOutputSchema,
  easyToCompete: scoringCriterionOutputSchema,
  profitPotential: scoringCriterionOutputSchema,
  _notes: z.string().optional(),
})
export type ScoringAIOutput = z.infer<typeof scoringAIOutputSchema>

export const briefAIOutputSchema = briefContentSchema.extend({
  _notes: z.string().optional(),
})
export type BriefAIOutput = z.infer<typeof briefAIOutputSchema>

// ============================================================================
// API REQUEST SCHEMAS (Zod) — routes validate inputs with these
// ============================================================================

export const sectorSchema = z.enum(SECTORS)
export const segmentStatusSchema = z.enum(SEGMENT_STATUSES)
export const personaRoleSchema = z.enum(PERSONA_ROLES)
export const coverageLevelSchema = z.enum(COVERAGE_LEVELS)

export const createSegmentSchema = z.object({
  name: z.string().min(1).max(200),
  sector: sectorSchema.default('private'),
  vertical: z.string().max(200).optional().nullable(),
  description: z.string().max(2000).optional().nullable(),
  status: segmentStatusSchema.default('active'),
  notes: z.string().max(5000).optional().nullable(),
})
export type CreateSegmentInput = z.infer<typeof createSegmentSchema>

export const updateSegmentSchema = createSegmentSchema.partial()
export type UpdateSegmentInput = z.infer<typeof updateSegmentSchema>

/** Field update: `path` picks the JSONB blob, `key` the field within it. */
export const updateFieldSchema = z.object({
  path: z.enum(['sizing', 'scoring']),
  key: z.string().min(1),
  value: z.number().nullable(),
})
export type UpdateFieldInput = z.infer<typeof updateFieldSchema>

export const resetFieldSchema = z.object({
  path: z.enum(['sizing', 'scoring']),
  key: z.string().min(1),
})
export type ResetFieldInput = z.infer<typeof resetFieldSchema>

export const reorderSegmentsSchema = z.object({
  orderedIds: z.array(z.string().uuid()).min(1),
})
export type ReorderSegmentsInput = z.infer<typeof reorderSegmentsSchema>

export const createPersonaSchema = z.object({
  name: z.string().min(1).max(200),
  role: personaRoleSchema.default('economic_buyer'),
  description: z.string().max(2000).optional().nullable(),
  linkedWorkshopPersonaId: z.string().max(200).optional().nullable(),
})
export type CreatePersonaInput = z.infer<typeof createPersonaSchema>

export const updatePersonaSchema = createPersonaSchema.partial()
export type UpdatePersonaInput = z.infer<typeof updatePersonaSchema>

export const linkPersonaSchema = z.object({
  personaId: z.string().uuid(),
  coverage: coverageLevelSchema.default('primary'),
  notes: z.string().max(500).optional().nullable(),
})
export type LinkPersonaInput = z.infer<typeof linkPersonaSchema>

export const setBeachheadSchema = z.object({
  segmentId: z.string().uuid().nullable(),
})
export type SetBeachheadInput = z.infer<typeof setBeachheadSchema>

/** AI generation request — apiKey required; route validates, service consumes. */
export const aiGenerateRequestSchema = z.object({
  apiKey: z.string().min(1),
})
export type AIGenerateRequest = z.infer<typeof aiGenerateRequestSchema>

// ============================================================================
// CANVAS BUNDLE (the GET /canvas response shape)
// ============================================================================

export interface MarketSegmentationCanvasBundle {
  canvas: MarketSegmentationCanvas
  segments: MarketSegmentWithScore[]
  personas: MarketPersona[]
  coverage: SegmentPersonaLink[]
  briefsCurrent: Array<{
    segmentId: string
    briefId: string
    version: number
    generatedAt: string
  }>
}
